import { useState, useEffect, useMemo, useRef } from "react";
import CreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Search, Hourglass, AlertTriangle, Clock, Briefcase, Archive, Crown, Plus, X, UserPlus, DollarSign, Trash2 } from 'lucide-react';
import { capitalizeFirst, isNewTeam, isClosingSoon, getStatusColor, renderClosingRibbon, getAccessToken } from "../common/utils";
import { getDashboardNew, createTeam, updateTeam } from "../config/ApiConfig";
import { searchEmployees, loadEmployees, hasEmployeeDirectoryError } from "../services/employeeDirectory";

const TeamsOverview = () => {
    const [customerInfo, setCustomerInfo] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        
        const customerName = searchParams.get('customer');
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await getDashboardNew(customerName);
                setCustomerInfo(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching data:', error);
                setCustomerInfo([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [searchParams]);

    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);

    const [filters, setFilters] = useState({
        newTeams: false,
        atRisk: false,
        hasInvestment: false,
        needsUpdate: false,
        showCompleted: false,
        closingSoon: false
    });

    useEffect(() => {
        const filterParam = searchParams.get('filter');
        if (filterParam === 'new') {
            setFilters(prev => ({ ...prev, newTeams: true }));
        } else if (filterParam === 'atRisk') {
            setFilters(prev => ({ ...prev, atRisk: true }));
        }
    }, [searchParams]);

    const [teamFormData, setTeamFormData] = useState({
        customer: '',
        businessUnit: '',
        team: '',
        startDate: '',
        endDate: '',
        members: []
    });

    const allTeams = useMemo(() => {
        if (!Array.isArray(customerInfo)) {
            console.warn('customerInfo is not an array:', customerInfo);
            return [];
        }
        return customerInfo.map(team => ({
            ...team,
            name: team.team,
            teamStatus: team.endDate && new Date(team.endDate) < new Date() ? 'Completed' : 'Active'
        }));
    }, [customerInfo]);

    const needsStatusUpdate = (team) => {
        if (!team.lastUpdated) return true;
        const lastUpdate = new Date(team.lastUpdated);
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        return lastUpdate < oneMonthAgo;
    };

    const filteredTeams = useMemo(() => {
        return allTeams.filter(team => {
            const teamHealth = team.teamHealth || '';
            const relationshipHealth = team.relationshipHealth || '';
            const deliveryHealth = team.deliveryHealth || '';
            const metrics = [teamHealth, relationshipHealth, deliveryHealth];
            const allStartStrong = metrics.every(status => status === 'Start Strong');
            const hasRiskyMetric = metrics.some(status => status === 'At Risk' || status === 'Critical');

            if (!filters.showCompleted && team.teamStatus === 'Completed') return false;
            if (filters.showCompleted && team.teamStatus !== 'Completed') return false;

            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const matchesSearch =
                    team.name.toLowerCase().includes(searchLower) ||
                    team.customer.toLowerCase().includes(searchLower) ||
                    (team.businessUnit?.toLowerCase().includes(searchLower)) ||
                    (team.members?.some(member => member.isCaptain && member.name.toLowerCase().includes(searchLower)));
                if (!matchesSearch) return false;
            }

            if (filters.newTeams && !isNewTeam(team.startDate)) return false;
            if (filters.atRisk && (!hasRiskyMetric || allStartStrong)) return false;
            if (filters.hasInvestment && !team.members?.some(member => !!member.isInvestment)) return false;
            if (filters.needsUpdate && !needsStatusUpdate(team)) return false;
            if (filters.closingSoon && !isClosingSoon(team.endDate).isClosingSoon) return false;

            return true;
        }).sort((a, b) => {
            const customerA = a.customer || '';
            const customerB = b.customer || '';
            const customerCompare = customerA.localeCompare(customerB);
            if (customerCompare !== 0) return customerCompare;

            const buA = a.businessUnit || '';
            const buB = b.businessUnit || '';
            const buCompare = buA.localeCompare(buB);
            if (buCompare !== 0) return buCompare;

            const nameA = a.name || '';
            const nameB = b.name || '';
            return nameA.localeCompare(nameB);
        });
    }, [allTeams, searchTerm, filters]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleCreateTeam = () => {
        setTeamFormData({
            customer: '',
            businessUnit: '',
            team: '',
            startDate: '',
            endDate: '',
            members: [],
            teamHealth: 'Start Strong',
            relationshipHealth: 'Start Strong',
            deliveryHealth: 'Start Strong',
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const teamInfo = { ...teamFormData };
        const updatedTimestamp = new Date().toISOString();
        const toastId = toast.loading('Creation In Progress...');

        try {
            await createTeam(teamInfo);
            console.log('Team created successfully');
            toast.success('Team has been created!', { id: toastId });
            setCustomerInfo(prev => [...prev, { ...teamInfo, lastUpdated: updatedTimestamp }]);
            handleCloseModal();
        } catch (error) {
            console.error('Error creating team:', error);
            toast.error('Team creation failed. Please try again.', { id: toastId });
        }
    };

    const addMember = () => {
        const newMember = {
            id: `member-${Date.now()}`,
            name: '',
            role: '',
            employeeId: '',
            isCaptain: false,
            isInvestment: false
        };
        setTeamFormData(prev => ({
            ...prev,
            members: [...prev.members, newMember]
        }));
    };

    const updateMember = (index, field, value) => {
        setTeamFormData(prev => ({
            ...prev,
            members: prev.members.map((member, i) =>
                i === index ? { ...member, [field]: value } : member
            )
        }));
    };

    const removeMember = (index) => {
        setTeamFormData(prev => ({
            ...prev,
            members: prev.members.filter((_, i) => i !== index)
        }));
    };

    const setCaptain = (memberId) => {
        setTeamFormData(prev => ({
            ...prev,
            members: prev.members.map(member => ({
                ...member,
                isCaptain: member.id === memberId ? !member.isCaptain : member.isCaptain
            }))
        }));
    };

    const handleHealthStatusChange = async (team, healthType, newStatus) => {
        const oldStatus = team[healthType];
        if (oldStatus === newStatus) {
            console.log(`No change in ${healthType} for team ${team.name}`);
            return;
        }

        const toastId = toast.loading('Updating...');
        const { teamStatus, name, ...teamData } = team;
        const updatedTimestamp = new Date().toISOString();

        try {
            await updateTeam({
                ...teamData,
                [healthType]: newStatus
            }, team.customer, team.team);

            setCustomerInfo(prev => prev.map(t =>
                t.customer === team.customer && t.team === team.team ? { ...t, [healthType]: newStatus, lastUpdated: updatedTimestamp } : t
            ));
            console.log('Health status updated successfully');
            toast.success('Update successful!', { id: toastId });
        } catch (error) {
            console.error('Error updating health status:', error);
            toast.error('Update failed. Please try again.', { id: toastId });
        }
    };

    const HealthStatusDropdown = ({ currentStatus, onChange }) => {
        const [isOpen, setIsOpen] = useState(false);
        const dropdownRef = useRef(null);
        const statuses = ['Start Strong', 'Healthy', 'At Risk', 'Critical'];

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        return (
            <div className="relative" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`px-3 py-1.5 inline-flex items-center text-sm font-medium rounded-full 
                                ${getStatusColor(currentStatus)} 
                                shadow-sm hover:shadow-md transition-all duration-200 
                                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 
                                hover:scale-[1.03] active:scale-[0.98]`}
                >
                    {currentStatus}
                </button>
                {isOpen && (
                    <div className="absolute z-10 mt-1 w-24 bg-white border border-gray-200 rounded-md shadow-lg">
                        {statuses.map(status => (
                            <button
                                key={status}
                                onClick={() => {
                                    onChange(status);
                                    setIsOpen(false);
                                }}
                                className={`block w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${status === currentStatus ? 'bg-gray-100' : ''
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4">
                <Link to="/dashboard" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
                    <ArrowLeft className="w-5 h-5 mr-1" />
                    Back to Dashboard
                </Link>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <h1 className="text-3xl font-bold text-gray-900">Manage Teams</h1>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
                        <button
                            onClick={handleCreateTeam}
                            className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Create New Team
                        </button>
                        <div className="relative w-full sm:w-80">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search teams, customers, or captains..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-wrap gap-4">
                        <button
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${filters.showCompleted ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            onClick={() => setFilters(f => ({ ...f, showCompleted: !f.showCompleted }))}
                        >
                            <Archive className="w-4 h-4" />
                            <span>Completed Teams</span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${filters.newTeams ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            onClick={() => setFilters(f => ({ ...f, newTeams: !f.newTeams }))}
                        >
                            <Clock className="w-4 h-4" />
                            <span>New Teams</span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${filters.atRisk ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            onClick={() => setFilters(f => ({ ...f, atRisk: !f.atRisk }))}
                        >
                            <AlertTriangle className="w-4 h-4" />
                            <span>At Risk</span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${filters.hasInvestment ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            onClick={() => setFilters(f => ({ ...f, hasInvestment: !f.hasInvestment }))}
                        >
                            <Briefcase className="w-4 h-4" />
                            <span>Has Investment</span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${filters.needsUpdate ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            onClick={() => setFilters(f => ({ ...f, needsUpdate: !f.needsUpdate }))}
                        >
                            <Clock className="w-4 h-4" />
                            <span>Needs Update</span>
                        </button>
                        <button
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${filters.closingSoon ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            onClick={() => setFilters(f => ({ ...f, closingSoon: !f.closingSoon }))}
                        >
                            <Hourglass className="w-4 h-4" />
                            <span>Closing Soon</span>
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="overflow-x-auto">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid" />
                                <p className="mt-4 text-gray-600">Loading data...</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Customer / Business Unit
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Team Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            End Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Captain
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Team Health
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Relationship Health
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Delivery Health
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Updated
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTeams.map((team) => {
                                        const captains = team.members?.filter(member => member.isCaptain) || [];
                                        return (
                                            <tr key={team.customer + team.name + team.lastUpdated} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <div className="text-sm font-medium text-gray-900">{team.customer}</div>
                                                    <div className="text-sm text-gray-500">{team.businessUnit}</div>
                                                </td>
                                                <td className="px-6 py-4 align-middle">
                                                    <div className="flex items-center">
                                                        <div className="text-sm font-medium text-gray-900 max-w-[18rem] sm:max-w-[22rem] md:max-w-[26rem] lg:max-w-[32rem] whitespace-normal break-words">
                                                            {team.name}
                                                            {team.teamStatus !== 'Completed' && !isClosingSoon(team.endDate).isClosingSoon && isNewTeam(team.startDate) && (
                                                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                                                                    New
                                                                </span>
                                                            )}
                                                            {team.teamStatus === 'Completed' && (
                                                                <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-800 rounded-full text-xs">
                                                                    Completed
                                                                </span>
                                                            )}
                                                            {renderClosingRibbon(team.endDate)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">
                                                    {team.endDate && !isNaN(new Date(team.endDate).getTime()) ? formatDate(team.endDate) : (
                                                        <span className="text-gray-400 italic">Ongoing</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {captains.length > 0 ? (
                                                        <div className="space-y-1">
                                                            {captains.map((captain, idx) => (
                                                                <div key={captain.id || idx} className="flex items-center">
                                                                    <div>
                                                                        <div className="flex items-center">
                                                                            <span className="text-sm font-medium text-gray-900">{captain.name}</span>
                                                                        </div>
                                                                        <div className="text-xs text-gray-500">Lead</div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 italic">No captain assigned</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <HealthStatusDropdown
                                                        currentStatus={team.teamHealth || 'N/A'}
                                                        onChange={(status) => handleHealthStatusChange(team, 'teamHealth', status)}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <HealthStatusDropdown
                                                        currentStatus={team.relationshipHealth || 'N/A'}
                                                        onChange={(status) => handleHealthStatusChange(team, 'relationshipHealth', status)}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <HealthStatusDropdown
                                                        currentStatus={team.deliveryHealth || 'N/A'}
                                                        onChange={(status) => handleHealthStatusChange(team, 'deliveryHealth', status)}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {team.lastUpdated ? (
                                                        <div className={needsStatusUpdate(team) ? 'text-red-600' : 'text-gray-500'}>
                                                            {formatDate(team.lastUpdated)}
                                                            {needsStatusUpdate(team) && (
                                                                <div className="text-xs text-red-500">Needs update</div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-red-600">Never updated</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm font-medium">
                                                    <div className="flex items-center space-x-2">
                                                        <Link
                                                            to={`/team/${team.customer}/${team.name}`}
                                                            className="text-blue-600 hover:text-blue-900"
                                                        >
                                                            View
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {filteredTeams.length === 0 && !isLoading && (
                        <div className="text-center py-12">
                            <div className="text-gray-500 text-lg">No teams found</div>
                            <div className="text-gray-400 text-sm mt-2">
                                Try adjusting your search criteria or filters
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Team Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">
                                Create New Team
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Customer Name
                                    </label>
                                    <CreatableSelect
                                        isClearable
                                        options={[...new Set(customerInfo.map(client => client.customer))]
                                            .filter(Boolean)
                                            .sort((a, b) => a.localeCompare(b))
                                            .map(customer => ({
                                                label: customer,
                                                value: customer
                                            }))
                                        }
                                        value={teamFormData.customer ? { label: teamFormData.customer, value: teamFormData.customer } : null}
                                        onChange={(selected) => setTeamFormData(prev => ({ ...prev, customer: selected ? capitalizeFirst(selected.value) : '', businessUnit: '' }))}
                                        placeholder="Select or enter a customer"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Business Unit
                                    </label>
                                    <CreatableSelect
                                        isClearable
                                        isDisabled={!teamFormData.customer}
                                        options={teamFormData.customer ? [...new Set(customerInfo
                                            .filter(c => c.customer === teamFormData.customer)
                                            .map(c => c.businessUnit))]
                                            .filter(Boolean)
                                            .sort((a, b) => a.localeCompare(b))
                                            .map(bu => ({
                                                label: bu,
                                                value: bu
                                            })) : []
                                        }
                                        value={teamFormData.businessUnit ? { label: teamFormData.businessUnit, value: teamFormData.businessUnit } : null}
                                        onChange={(selected) => setTeamFormData(prev => ({ ...prev, businessUnit: selected ? capitalizeFirst(selected.value) : '' }))}
                                        placeholder={teamFormData.customer ? 'Select or enter business unit' : 'Select a customer first'}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Team Name
                                    </label>
                                    <input
                                        type="text"
                                        value={teamFormData.team}
                                        onChange={(e) => setTeamFormData(prev => ({ ...prev, team: capitalizeFirst(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter team name"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={teamFormData.startDate}
                                        onChange={(e) => setTeamFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Date (Optional)
                                    </label>
                                    <input
                                        type="date"
                                        value={teamFormData.endDate}
                                        onChange={(e) => setTeamFormData(prev => ({ ...prev, endDate: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-4 border-t pt-6">
                                    <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
                                    <button
                                        type="button"
                                        onClick={addMember}
                                        className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                    >
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Add Member
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {teamFormData.members.map((member, index) => (
                                        <div
                                            key={member.id}
                                            className="flex flex-col space-y-2 p-4 border border-gray-200 rounded-lg"
                                        >
                                            <div className="flex items-center space-x-4">
                                                <div className="flex-1">
                                                    <AsyncSelect
                                                        cacheOptions
                                                        defaultOptions
                                                        isClearable
                                                        placeholder="Name (type to search)"
                                                        noOptionsMessage={() => "No employees found"}
                                                        loadOptions={async (inputValue) => {
                                                            try {
                                                                const results = await searchEmployees(inputValue);
                                                                return results.map(e => ({
                                                                    value: e.id,
                                                                    label: e.name + (e.employeeId ? ` (${e.employeeId})` : ''),
                                                                    meta: e
                                                                }));
                                                            } catch (err) {
                                                                return [];
                                                            }
                                                        }}
                                                        onChange={(opt) => {
                                                            if (opt && opt.meta) {
                                                                updateMember(index, 'name', capitalizeFirst(opt.meta.name));
                                                                updateMember(index, 'employeeId', opt.meta.employeeId || opt.meta.id);
                                                            } else {
                                                                updateMember(index, 'name', '');
                                                                updateMember(index, 'employeeId', '');
                                                            }
                                                        }}
                                                        value={member.name ? { value: member.employeeId || member.name, label: member.name, meta: null } : null}
                                                        className="w-full"
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Role"
                                                    value={member.role}
                                                    onChange={(e) => updateMember(index, 'role', capitalizeFirst(e.target.value))}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    required
                                                />
                                                <label className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={member.isInvestment || false}
                                                        onChange={(e) => updateMember(index, 'isInvestment', e.target.checked)}
                                                        className="mr-2"
                                                    />
                                                    <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                                                    <span className="text-sm text-gray-700">Investment</span>
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => setCaptain(member.id)}
                                                    className={`px-3 py-2 rounded-md flex items-center ${member.isCaptain === true
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    <Crown className="w-4 h-4 mr-1" />
                                                    Captain
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeMember(index)}
                                                    className="px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {member.isInvestment && (
                                                <div className="w-full bg-gray-50 p-3 rounded-md border border-gray-200">
                                                    <div className="flex space-x-4">
                                                        <div className="flex-1">
                                                            <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                                                            <input
                                                                type="date"
                                                                onChange={(e) => updateMember(index, 'investmentStartDate', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                required
                                                            />
                                                        </div>
                                                        <div className="flex-1">
                                                            <label className="block text-sm text-gray-600 mb-1">Duration (in months)</label>
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                step="1"
                                                                onChange={(e) => updateMember(index, 'investmentDuration', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-4 pt-8">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Create Team
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );


};

export default TeamsOverview;